import { c as _c } from "react/compiler-runtime";
import { useSpring, config } from '@react-spring/core';
import { animated } from '@react-spring/web';
import React, { FunctionComponent, ComponentProps } from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { IconDeprecated, IconSize, IconType } from '../../misc/IconDeprecated/IconDeprecated';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { Notification, NotificationIconType, NotificationType } from '../classes/NotificationManager/NotificationManager';
import styles from './NotificationCard.module.css';
export type NotificationCardProps = PropsWithoutChildren<Omit<ComponentProps<'div'>, 'style' | 'ref'>> & {
  notification: Notification;
  onCloseButtonClick: (notification: Notification) => void;
  onHidden: (notification: Notification) => void;
};
export const NotificationCard: FunctionComponent<NotificationCardProps> = React.memo(props => {
  const $ = _c(21);
  const {
    notification,
    className,
    onCloseButtonClick,
    onHidden,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const iconConfig = iconConfigByType[notification.iconType];
  const style = useSpring({
    from: {
      transform: "translateX(20%)",
      opacity: 0
    },
    to: {
      transform: "translateX(0%)",
      opacity: 1
    },
    reverse: !notification.show,
    config: {
      ...config.stiff,
      restVelocity: 0.005
    },
    onRest: () => {
      if (!notification.show) {
        onHidden(notification);
      }
    }
  });
  const handleCloseButtonClick = useEventCallback(() => {
    onCloseButtonClick(notification);
  });
  const t0 = animated;
  const t1 = classes(className, styles.card, cardClassNameByType[notification.type]);
  let t2;
  if ($[0] !== iconConfig) {
    t2 = iconConfig ? <IconDeprecated {...iconConfig} className={styles.icon} data-testid="NotificationCard.Icon" /> : undefined;
    $[0] = iconConfig;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== notification.title) {
    t3 = <div className={styles.title}>{notification.title}</div>;
    $[2] = notification.title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconDeprecated name="times" size={IconSize.Small} />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== handleCloseButtonClick) {
    t5 = <button type="button" className={styles.closeButton} onClick={handleCloseButtonClick} data-testid="NotificationCard.CloseButton">{t4}</button>;
    $[5] = handleCloseButtonClick;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t5) {
    t6 = <div className={styles.titleRow}>{t2}{t3}{t5}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== iconConfig || $[12] !== notification.message) {
    t7 = notification.message ? <div className={classes(styles.message, iconConfig ? styles.messageWithIcon : null)}>{notification.message}</div> : undefined;
    $[11] = iconConfig;
    $[12] = notification.message;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== remainingProps || $[15] !== style || $[16] !== t0.div || $[17] !== t1 || $[18] !== t6 || $[19] !== t7) {
    t8 = <t0.div {...remainingProps} className={t1} style={style}>{t6}{t7}</t0.div>;
    $[14] = remainingProps;
    $[15] = style;
    $[16] = t0.div;
    $[17] = t1;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
});
const cardClassNameByType: Record<NotificationType, string | null> = {
  [NotificationType.Info]: styles.infoCard,
  [NotificationType.Success]: styles.successCard,
  [NotificationType.Error]: styles.errorCard
};
const iconConfigByType: Record<NotificationIconType, {
  name: string;
  type: IconType;
} | null> = {
  [NotificationIconType.None]: null,
  [NotificationIconType.Success]: {
    name: 'check-circle',
    type: IconType.Regular
  },
  [NotificationIconType.Info]: {
    name: 'info-circle',
    type: IconType.Regular
  },
  [NotificationIconType.Error]: {
    name: 'times-circle',
    type: IconType.Regular
  }
};